import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Drawer,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  List as ListIcon,
  ArrowForward as ArrowForwardIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import apiClient from '../../../lib/api';

const FuneralCompanyDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    description: '',
    website: '',
    contactPersonName: '',
    phone: '',
    email: '',
  });
  const [originalData, setOriginalData] = useState({
    companyName: '',
    description: '',
    website: '',
    contactPersonName: '',
    phone: '',
    email: '',
  });
  const [errors, setErrors] = useState({});
  const [owner, setOwner] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [isOwnerDrawerOpen, setIsOwnerDrawerOpen] = useState(false);
  const [ownerFormData, setOwnerFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [ownerErrors, setOwnerErrors] = useState({});
  const [creatingOwner, setCreatingOwner] = useState(false);
  const [stripeConnectAccount, setStripeConnectAccount] = useState('');
  const [stripeAccountDetails, setStripeAccountDetails] = useState(null);
  const [loadingStripeAccount, setLoadingStripeAccount] = useState(false);
  const [stripeModalOpen, setStripeModalOpen] = useState(false);
  const [stripeAccountIdInput, setStripeAccountIdInput] = useState('');
  const [savingStripeAccount, setSavingStripeAccount] = useState(false);
  const [modalAccountDetails, setModalAccountDetails] = useState(null);
  const [loadingModalAccount, setLoadingModalAccount] = useState(false);

  // Fetch funeral company data on component mount
  useEffect(() => {
    if (id && id !== 'new') {
      fetchFuneralCompany();
    }
  }, [id]);

  const fetchFuneralCompany = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getFuneralCompany(id);
      console.log('Funeral Company API Response:', response);
      
      const company = response.company || response.data || response;

      const data = {
        companyName: company.companyName || '',
        description: company.description || '',
        website: company.website || '',
        contactPersonName: company.contactPersonName || '',
        phone: company.phone || '',
        email: company.email || '',
        ownerId: company.ownerId || null,
      };

      console.log('Parsed Funeral Company Data:', data);
      setFormData(data);
      setOriginalData(data);

      // Set Stripe Connect account
      setStripeConnectAccount(company.stripeConnectAccount || '');

      // Fetch Stripe Connect account details if account ID exists
      if (company.stripeConnectAccount) {
        fetchStripeAccountDetails(company.stripeConnectAccount);
      } else {
        setStripeAccountDetails(null);
      }

      // Fetch owner if ownerId exists
      if (company.ownerId) {
        fetchOwner(company.ownerId);
      }
    } catch (error) {
      console.error('Error fetching funeral company:', error);
      toast.error('Failed to fetch funeral company');
    } finally {
      setLoading(false);
    }
  };

  const fetchOwner = async (ownerId) => {
    try {
      const ownerIdToFetch = ownerId || originalData.ownerId;
      if (!ownerIdToFetch) {
        return;
      }

      const response = await apiClient.getUser(ownerIdToFetch);
      const ownerData = response.user || response.data || response;
      setOwner(ownerData);
    } catch (error) {
      console.error('Error fetching owner:', error);
      // Don't show error toast - owner might not exist
    }
  };

  const handleSendInvite = async () => {
    if (!owner || !owner.email) {
      toast.error('Owner information not available');
      return;
    }

    setSendingInvite(true);
    try {
      await apiClient.sendFuneralDirectorInvite(owner.id, {
        email: owner.email,
        firstName: owner.firstName || '',
        lastName: owner.lastName || '',
      });
      toast.success('Invite sent successfully');
    } catch (error) {
      console.error('Error sending invite:', error);
      const errorMessage = error.response?.data?.error || 'Error sending invite';
      toast.error(errorMessage);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleOpenOwnerDrawer = () => {
    setIsOwnerDrawerOpen(true);
    setOwnerFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    });
    setOwnerErrors({});
  };

  const handleCloseOwnerDrawer = () => {
    setIsOwnerDrawerOpen(false);
    setOwnerFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    });
    setOwnerErrors({});
  };

  const handleOwnerInputChange = (field, value) => {
    setOwnerFormData(prev => ({ ...prev, [field]: value }));
    if (ownerErrors[field]) {
      setOwnerErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateOwnerForm = () => {
    const newErrors = {};

    if (!ownerFormData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!ownerFormData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!ownerFormData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(ownerFormData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!ownerFormData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (ownerFormData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (ownerFormData.password !== ownerFormData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setOwnerErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateOwner = async () => {
    if (!validateOwnerForm()) {
      toast.error('Please fix all validation errors');
      return;
    }

    setCreatingOwner(true);
    try {
      // Create user as funeral director
      const userData = {
        firstName: ownerFormData.firstName,
        lastName: ownerFormData.lastName,
        email: ownerFormData.email,
        phone: ownerFormData.phone || undefined,
        password: ownerFormData.password,
        type: 'funeral_director',
        companyId: id, // Associate with this company
      };

      const response = await apiClient.createFuneralDirector(userData);
      const newOwner = response.user || response.data || response;

      // Update company with ownerId
      await apiClient.updateFuneralCompany(id, {
        ownerId: newOwner.id,
      });

      // Update local state
      setOwner(newOwner);
      setOriginalData(prev => ({ ...prev, ownerId: newOwner.id }));
      
      toast.success('Owner created successfully');
      handleCloseOwnerDrawer();
    } catch (error) {
      console.error('Error creating owner:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to create owner';
      toast.error(errorMessage);
    } finally {
      setCreatingOwner(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }
    if (!formData.contactPersonName.trim()) {
      newErrors.contactPersonName = 'Contact person name is required';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
    setErrors({});
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setFormData(originalData);
    setErrors({});
  };

  const fetchStripeAccountDetails = async (accountId) => {
    if (!accountId) {
      setStripeAccountDetails(null);
      return;
    }

    setLoadingStripeAccount(true);
    try {
      const response = await apiClient.getStripeConnectAccount(accountId);
      setStripeAccountDetails(response.data || response);
    } catch (error) {
      console.error('Error fetching Stripe account details:', error);
      setStripeAccountDetails(null);
    } finally {
      setLoadingStripeAccount(false);
    }
  };

  const handleOpenStripeModal = () => {
    setStripeAccountIdInput(stripeConnectAccount || '');
    setModalAccountDetails(null);
    setStripeModalOpen(true);
  };

  const handleCloseStripeModal = () => {
    setStripeModalOpen(false);
    setStripeAccountIdInput('');
    setModalAccountDetails(null);
  };

  const handleSaveStripeAccount = async () => {
    setSavingStripeAccount(true);
    try {
      await apiClient.updateFuneralCompany(id, { stripeConnectAccount: stripeAccountIdInput });
      toast.success('Stripe Connect account updated successfully');
      
      // Update local state and fetch account details
      setStripeConnectAccount(stripeAccountIdInput);
      
      if (stripeAccountIdInput) {
        await fetchStripeAccountDetails(stripeAccountIdInput);
      } else {
        setStripeAccountDetails(null);
      }
      
      handleCloseStripeModal();
    } catch (error) {
      console.error('Error updating Stripe account:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Error updating Stripe Connect account';
      toast.error(errorMessage);
    } finally {
      setSavingStripeAccount(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix all validation errors');
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        companyName: formData.companyName,
        description: formData.description,
        website: formData.website,
        contactPersonName: formData.contactPersonName,
        phone: formData.phone,
        email: formData.email,
      };

      console.log('Submitting data:', submitData);
      await apiClient.updateFuneralCompany(id, submitData);
      
      setOriginalData(formData);
      toast.success('Funeral Company updated successfully!');
      setIsDrawerOpen(false);
    } catch (error) {
      console.error('Error updating funeral company:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update funeral company';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout 
        title="Funeral Company Details"
        breadcrumbData={{ companyName: 'Company' }}
      >
        <Box sx={{ p: 3 }}>
          <Typography>Loading...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Funeral Company Details"
      breadcrumbData={{ companyName: originalData.companyName || 'Company' }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">
                {originalData.companyName || 'Funeral Company'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage company information
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* View All Branches button */}
            <Button
              variant="outlined"
              onClick={() => navigate(`/companies/${id}/branches`)}
              startIcon={<ListIcon />}
              endIcon={<ArrowForwardIcon />}
              sx={{ 
                minWidth: { xs: '100%', sm: 180 },
                borderColor: '#2c4575',
                color: '#2c4575',
                '&:hover': {
                  borderColor: '#1e3154',
                  backgroundColor: 'rgba(44, 69, 117, 0.04)'
                }
              }}
            >
              View All Branches
            </Button>
            
            {/* Add Owner button - show if no owner exists */}
            {!owner && (
              <Button
                variant="outlined"
                onClick={handleOpenOwnerDrawer}
                startIcon={<PersonIcon />}
                sx={{ 
                  minWidth: { xs: '100%', sm: 150 },
                  borderColor: '#2c4575',
                  color: '#2c4575',
                  '&:hover': {
                    borderColor: '#1e3154',
                    backgroundColor: 'rgba(44, 69, 117, 0.04)'
                  }
                }}
              >
                Add Owner
              </Button>
            )}
            
            {/* Send Invite button - show if owner exists */}
            {owner && (
              <Button
                variant="outlined"
                onClick={handleSendInvite}
                disabled={sendingInvite}
                startIcon={sendingInvite ? null : <EmailIcon />}
                sx={{ 
                  minWidth: { xs: '100%', sm: 150 },
                  borderColor: '#2c4575',
                  color: '#2c4575',
                  '&:hover': {
                    borderColor: '#1e3154',
                    backgroundColor: 'rgba(44, 69, 117, 0.04)'
                  }
                }}
              >
                {sendingInvite ? 'Sending...' : 'Send Invite'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Company Information Display */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Company Information
              </Typography>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleOpenDrawer}
              >
                Edit
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Company Name */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Company Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {originalData.companyName || '-'}
                </Typography>
              </Box>

              {/* Description */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Description
                </Typography>
                <Typography variant="body1">
                  {originalData.description || '-'}
                </Typography>
              </Box>

              {/* Website */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LanguageIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  <Typography variant="caption" color="text.secondary">
                    Website
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {originalData.website ? (
                    <a 
                      href={originalData.website} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      {originalData.website}
                    </a>
                  ) : '-'}
                </Typography>
              </Box>

              <Divider />

              {/* Contact Information Section */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Contact Information
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Contact Person
                    </Typography>
                    <Typography variant="body1">
                      {originalData.contactPersonName || '-'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                      Phone
                    </Typography>
                    <Typography variant="body1">
                      {originalData.phone || '-'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      <EmailIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {originalData.email || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Stripe Connect Account Card */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon />
                <Typography variant="h6">Stripe Connect Account</Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={handleOpenStripeModal}
                startIcon={<CreditCardIcon />}
                sx={{ 
                  borderColor: '#2c4575',
                  color: '#2c4575',
                  '&:hover': {
                    borderColor: '#1e3154',
                    backgroundColor: 'rgba(44, 69, 117, 0.04)'
                  }
                }}
              >
                {stripeConnectAccount ? 'Change Account' : 'Set Account'}
              </Button>
            </Box>

            {stripeConnectAccount ? (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Account ID:
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {stripeConnectAccount}
                  </Typography>
                </Box>

                {loadingStripeAccount ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading account details...</Typography>
                  </Box>
                ) : stripeAccountDetails ? (
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Account Status
                      </Typography>
                      <Chip 
                        label={
                          stripeAccountDetails.details_submitted && 
                          stripeAccountDetails.charges_enabled && 
                          stripeAccountDetails.payouts_enabled 
                            ? 'Verified' 
                            : 'Pending'
                        }
                        size="small"
                        color={
                          stripeAccountDetails.details_submitted && 
                          stripeAccountDetails.charges_enabled && 
                          stripeAccountDetails.payouts_enabled 
                            ? 'success' 
                            : 'warning'
                        }
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Details Submitted:
                        </Typography>
                        <Typography variant="body2">
                          {stripeAccountDetails.details_submitted ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Charges Enabled:
                        </Typography>
                        <Typography variant="body2">
                          {stripeAccountDetails.charges_enabled ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Payouts Enabled:
                        </Typography>
                        <Typography variant="body2">
                          {stripeAccountDetails.payouts_enabled ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                      {stripeAccountDetails.business_type && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            Business Type:
                          </Typography>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {stripeAccountDetails.business_type}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="warning">
                    Unable to load account details. Please verify the account ID is correct.
                  </Alert>
                )}
              </Box>
            ) : (
              <Alert severity="info">
                No Stripe Connect account configured. Click "Set Account" to add one.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Edit Drawer */}
        <Drawer
          anchor="right"
          open={isDrawerOpen}
          onClose={handleCloseDrawer}
          PaperProps={{
            sx: { 
              width: { xs: '100%', sm: 500 },
              backgroundColor: '#ffffff'
            }
          }}
        >
          <Box sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {/* Drawer Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Edit Company Information
              </Typography>
              <IconButton onClick={handleCloseDrawer} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Edit Form */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Company Name */}
              <TextField
                fullWidth
                label="Company Name"
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                error={!!errors.companyName}
                helperText={errors.companyName}
                required
              />

              {/* Description */}
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                error={!!errors.description}
                helperText={errors.description}
                multiline
                rows={3}
              />

              {/* Website */}
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                error={!!errors.website}
                helperText={errors.website}
                placeholder="https://example.com"
              />

              {/* Contact Information Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Contact Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Contact Person Name"
                    value={formData.contactPersonName}
                    onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                    error={!!errors.contactPersonName}
                    helperText={errors.contactPersonName}
                    required
                  />

                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    required
                  />

                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                  />
                </Box>
              </Box>
            </Box>

            {/* Drawer Action Buttons */}
            <Box sx={{ mt: 4 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading}
                startIcon={<SaveIcon />}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Drawer>

        {/* Add Owner Drawer */}
        <Drawer
          anchor="right"
          open={isOwnerDrawerOpen}
          onClose={handleCloseOwnerDrawer}
          PaperProps={{
            sx: { 
              width: { xs: '100%', sm: 500 },
              backgroundColor: '#ffffff'
            }
          }}
        >
          <Box sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {/* Drawer Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Create Owner
              </Typography>
              <IconButton onClick={handleCloseOwnerDrawer} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Owner Form */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="First Name"
                value={ownerFormData.firstName}
                onChange={(e) => handleOwnerInputChange('firstName', e.target.value)}
                error={!!ownerErrors.firstName}
                helperText={ownerErrors.firstName}
                required
              />

              <TextField
                fullWidth
                label="Last Name"
                value={ownerFormData.lastName}
                onChange={(e) => handleOwnerInputChange('lastName', e.target.value)}
                error={!!ownerErrors.lastName}
                helperText={ownerErrors.lastName}
                required
              />

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={ownerFormData.email}
                onChange={(e) => handleOwnerInputChange('email', e.target.value)}
                error={!!ownerErrors.email}
                helperText={ownerErrors.email}
                required
              />

              <TextField
                fullWidth
                label="Phone Number"
                value={ownerFormData.phone}
                onChange={(e) => handleOwnerInputChange('phone', e.target.value)}
                error={!!ownerErrors.phone}
                helperText={ownerErrors.phone}
              />

              <TextField
                fullWidth
                label="Password"
                type="password"
                value={ownerFormData.password}
                onChange={(e) => handleOwnerInputChange('password', e.target.value)}
                error={!!ownerErrors.password}
                helperText={ownerErrors.password}
                required
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={ownerFormData.confirmPassword}
                onChange={(e) => handleOwnerInputChange('confirmPassword', e.target.value)}
                error={!!ownerErrors.confirmPassword}
                helperText={ownerErrors.confirmPassword}
                required
              />
            </Box>

            {/* Drawer Action Buttons */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCloseOwnerDrawer}
                disabled={creatingOwner}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleCreateOwner}
                disabled={creatingOwner}
                startIcon={creatingOwner ? null : <SaveIcon />}
              >
                {creatingOwner ? 'Creating...' : 'Create Owner'}
              </Button>
            </Box>
          </Box>
        </Drawer>

        {/* Stripe Connect Account Modal */}
        <Dialog
          open={stripeModalOpen}
          onClose={handleCloseStripeModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CreditCardIcon />
              <Typography variant="h6">Stripe Connect Account</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                Changing the Stripe Connect account is a significant action. This will affect how payouts are processed for this funeral company.
              </Alert>
              <TextField
                fullWidth
                label="Stripe Connect Account ID"
                value={stripeAccountIdInput}
                onChange={(e) => setStripeAccountIdInput(e.target.value)}
                placeholder="acct_..."
                helperText="Enter the Stripe Connect account ID (e.g., acct_1234567890). Leave empty to remove the account."
                sx={{ mb: 2 }}
              />
              {stripeAccountIdInput && (
                <Button
                  variant="outlined"
                  onClick={async () => {
                    if (stripeAccountIdInput) {
                      setLoadingModalAccount(true);
                      try {
                        const response = await apiClient.getStripeConnectAccount(stripeAccountIdInput);
                        setModalAccountDetails(response.data || response);
                      } catch (error) {
                        console.error('Error fetching Stripe account details:', error);
                        setModalAccountDetails(null);
                      } finally {
                        setLoadingModalAccount(false);
                      }
                    }
                  }}
                  disabled={loadingModalAccount || !stripeAccountIdInput}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {loadingModalAccount ? 'Loading...' : 'Load Account Details'}
                </Button>
              )}
              {stripeAccountIdInput && loadingModalAccount && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading account details...</Typography>
                </Box>
              )}
              {stripeAccountIdInput && !loadingModalAccount && modalAccountDetails && (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  {/* Business Name */}
                  {(modalAccountDetails.company?.name || (modalAccountDetails.individual?.first_name && modalAccountDetails.individual?.last_name)) && (
                    <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Business Name:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {modalAccountDetails.company?.name || 
                         `${modalAccountDetails.individual?.first_name || ''} ${modalAccountDetails.individual?.last_name || ''}`.trim()}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Account Status
                    </Typography>
                    <Chip 
                      label={
                        modalAccountDetails.details_submitted && 
                        modalAccountDetails.charges_enabled && 
                        modalAccountDetails.payouts_enabled 
                          ? 'Verified' 
                          : 'Pending'
                      }
                      size="small"
                      color={
                        modalAccountDetails.details_submitted && 
                        modalAccountDetails.charges_enabled && 
                        modalAccountDetails.payouts_enabled 
                          ? 'success' 
                          : 'warning'
                      }
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Details Submitted:
                      </Typography>
                      <Typography variant="body2">
                        {modalAccountDetails.details_submitted ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Charges Enabled:
                      </Typography>
                      <Typography variant="body2">
                        {modalAccountDetails.charges_enabled ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Payouts Enabled:
                      </Typography>
                      <Typography variant="body2">
                        {modalAccountDetails.payouts_enabled ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                    {modalAccountDetails.business_type && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Business Type:
                        </Typography>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {modalAccountDetails.business_type}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {stripeAccountIdInput && !loadingModalAccount && !modalAccountDetails && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Unable to load account details. Please verify the account ID is correct.
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseStripeModal} disabled={savingStripeAccount}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveStripeAccount}
              variant="contained"
              disabled={savingStripeAccount}
            >
              {savingStripeAccount ? 'Saving...' : 'Save Account'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </DashboardLayout>
  );
};

export default FuneralCompanyDetailPage;
